/**
 * Dialog for configuring local development settings
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useConfig } from "@/lib/config/config-context";
import { configureTenantsSDK } from "@/lib/harmony-sdk/config";
import { useDescope, useSession } from "@descope/react-sdk";
import { Server, Key, User, AlertCircle } from "lucide-react";

interface LocalConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LocalConfigDialog({
  open,
  onOpenChange,
}: LocalConfigDialogProps) {
  const { config, updateConfig, isLocalMode } = useConfig();
  const { sessionToken, isAuthenticated } = useSession();
  const { getCurrentTenant } = useDescope();

  const [localBaseUrl, setLocalBaseUrl] = useState(config.localBaseUrl);
  const [localTenantId, setLocalTenantId] = useState(
    config.localTenantId ?? "",
  );
  const [isLoading, setIsLoading] = useState(false);

  // Get tenant ID from authenticated user if available
  const userTenantId = getCurrentTenant(sessionToken);

  useEffect(() => {
    setLocalBaseUrl(config.localBaseUrl);
    setLocalTenantId(config.localTenantId ?? "");
  }, [config, open]);

  const handleUseTenantId = () => {
    if (userTenantId) {
      setLocalTenantId(userTenantId);
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    try {
      updateConfig({
        localBaseUrl,
        localTenantId: localTenantId.trim() || undefined,
      });

      // Reconfigure SDK with new settings
      setTimeout(() => {
        configureTenantsSDK();
      }, 100);

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update local config:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setLocalBaseUrl(config.localBaseUrl);
    setLocalTenantId(config.localTenantId ?? "");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure Local Development</DialogTitle>
          <DialogDescription>
            Set up connection to your local Harmony backend server
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Local Server URL */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Server className="h-4 w-4" />
                Local Server URL
              </CardTitle>
              <CardDescription>
                The base URL of your local Harmony backend server
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="localBaseUrl">Base URL</Label>
                <Input
                  id="localBaseUrl"
                  value={localBaseUrl}
                  onChange={(e) => {
                    setLocalBaseUrl(e.target.value);
                  }}
                  placeholder="http://localhost:8000"
                  className="font-mono"
                />
              </div>
              <div className="text-muted-foreground text-sm">
                Make sure your local server is running on this URL. The API
                endpoints will be accessed at{" "}
                <code className="bg-muted rounded px-1">
                  {localBaseUrl}/api/v1
                </code>
              </div>
            </CardContent>
          </Card>

          {/* Tenant ID Configuration */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Key className="h-4 w-4" />
                Tenant ID for Requests
              </CardTitle>
              <CardDescription>
                The tenant ID to include in local API requests via x-tenant-id
                header
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Get tenant ID from authenticated user */}
              {isAuthenticated && userTenantId && (
                <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                  <User className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                  <div className="flex-1 space-y-2">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Authenticated User Tenant ID Available
                    </div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      Your authenticated user has tenant ID:{" "}
                      <code className="rounded bg-blue-100 px-1 font-mono dark:bg-blue-800/50">
                        {userTenantId}
                      </code>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUseTenantId}
                      className="border-blue-300 dark:border-blue-700"
                    >
                      Use This Tenant ID
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="localTenantId">Tenant ID</Label>
                <Input
                  id="localTenantId"
                  value={localTenantId}
                  onChange={(e) => {
                    setLocalTenantId(e.target.value);
                  }}
                  placeholder="Enter tenant ID for local development"
                  className="font-mono"
                />
              </div>

              <div className="text-muted-foreground text-sm">
                This tenant ID will be sent as the{" "}
                <code className="bg-muted rounded px-1">x-tenant-id</code>{" "}
                header with all API requests. Leave empty if your local server
                doesn't require tenant-specific requests.
              </div>
            </CardContent>
          </Card>

          {/* Current Mode Warning */}
          {!isLocalMode && (
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
              <div className="space-y-1">
                <div className="font-medium text-yellow-800 dark:text-yellow-200">
                  Currently in Remote Mode
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  These settings will only take effect when you switch to Local
                  Mode. You can change the operation mode in the Mode
                  Configuration dialog.
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
